import type { Middleware } from "@reduxjs/toolkit";
import type { ComponentType } from "react";
import { t } from "ttag";

import noResultsSource from "assets/img/no_results.svg";
import { PluginPlaceholder } from "metabase/plugins/components/PluginPlaceholder";
import type { UiParameter } from "metabase-lib/v1/parameters/types";
import type { Dashboard, IllustrationSettingValue } from "metabase-types/api";
import type {
  AdminPathKey,
  DraftDashboardSubscription,
  State,
} from "metabase-types/store";

// Types
export type IllustrationValue = {
  src: string;
  isDefault: boolean;
} | null;

interface PluginDashboardSubscriptionParametersSectionOverride {
  Component?: ComponentType<{
    className?: string;
    parameters: UiParameter[];
    hiddenParameters?: string;
    dashboard: Dashboard;
    pulse: DraftDashboardSubscription;
    setPulseParameters: (parameters: UiParameter[]) => void;
  }>;
}

// Lazy import helper to avoid circular dependency:
// core.ts → lib/settings → lib/utils → plugins → core.ts
// core.ts → selectors/settings → ... → plugins → core.ts
function lazyGetSetting(state: State, key: string): any {
  const { getSetting } = require("metabase/selectors/settings");
  return getSetting(state, key);
}

const getDefaultAppInitFunctions = (): (() => void)[] => [
  () => {
    const { mutateColors } = require("metabase/lib/colors/colors");
    const MetabaseSettings = require("metabase/lib/settings").default;
    const colors = MetabaseSettings.get("application-colors");
    if (colors) {
      mutateColors(colors);
    }
  },
];

export const PLUGIN_APP_INIT_FUNCTIONS = getDefaultAppInitFunctions();

const getDefaultLandingPage = () => ({
  getLandingPage: () => "/",
  LandingPageWidget: PluginPlaceholder,
});

export const PLUGIN_LANDING_PAGE: {
  getLandingPage: () => string | null | undefined;
  LandingPageWidget: ComponentType;
} = getDefaultLandingPage();

const getDefaultReduxMiddlewares = (): Middleware[] => [];

export const PLUGIN_REDUX_MIDDLEWARES = getDefaultReduxMiddlewares();

const getDefaultLogoIconComponents = (): ComponentType[] => [];

export const PLUGIN_LOGO_ICON_COMPONENTS = getDefaultLogoIconComponents();

const getDefaultAdminAllowedPathGetters = (): ((
  user: any,
) => AdminPathKey[])[] => [];

export const PLUGIN_ADMIN_ALLOWED_PATH_GETTERS =
  getDefaultAdminAllowedPathGetters();

const getDefaultAdminTools = () => ({
  COMPONENT: null,
});

export const PLUGIN_ADMIN_TOOLS: {
  COMPONENT: ComponentType | null;
} = getDefaultAdminTools();

const getDefaultLoadingMessage = (isSlow: boolean | undefined = false) =>
  isSlow ? t`Waiting for results...` : t`Doing science...`;

const getDefaultSelectors = () => ({
  canWhitelabel: (_state: State) => false,
  getLoadingMessageFactory: (state: State) => {
    const { LOADING_MESSAGE_BY_SETTING } = require("metabase/lib/loading-message");
    const setting = lazyGetSetting(state, "loading-message");
    return LOADING_MESSAGE_BY_SETTING[setting]?.value ?? getDefaultLoadingMessage;
  },
  getIsWhiteLabeling: (state: State) =>
    lazyGetSetting(state, "application-name") !== "Metabase",
  getApplicationName: (state: State) =>
    lazyGetSetting(state, "application-name"),
  getShowMetabaseLinks: (state: State) =>
    lazyGetSetting(state, "show-metabase-links"),
  getLoginPageIllustration: (state: State): IllustrationValue => {
    const option = lazyGetSetting(
      state,
      "login-page-illustration",
    ) as IllustrationSettingValue;
    switch (option) {
      case "default":
        return { src: "app/img/narvar-landing.webp", isDefault: true };
      case "none":
        return null;
      case "custom":
        return {
          src: lazyGetSetting(state, "login-page-illustration-custom") as string,
          isDefault: false,
        };
      default:
        return { src: "app/img/narvar-landing.webp", isDefault: true };
    }
  },
  getLandingPageIllustration: (state: State): IllustrationValue => {
    const option = lazyGetSetting(
      state,
      "landing-page-illustration",
    ) as IllustrationSettingValue;
    switch (option) {
      case "default":
        return { src: "app/img/narvar-landing.webp", isDefault: true };
      case "none":
        return null;
      case "custom":
        return {
          src: lazyGetSetting(state, "landing-page-illustration-custom") as string,
          isDefault: false,
        };
      default:
        return { src: "app/img/narvar-landing.webp", isDefault: true };
    }
  },
  getNoDataIllustration: (state: State): string | null => {
    const option = lazyGetSetting(
      state,
      "no-data-illustration",
    ) as IllustrationSettingValue;
    switch (option) {
      case "default":
        return noResultsSource;
      case "none":
        return null;
      case "custom":
        return lazyGetSetting(state, "no-data-illustration-custom") as string;
      default:
        return noResultsSource;
    }
  },
  getNoObjectIllustration: (state: State): string | null => {
    const option = lazyGetSetting(
      state,
      "no-object-illustration",
    ) as IllustrationSettingValue;
    switch (option) {
      case "default":
        return noResultsSource;
      case "none":
        return null;
      case "custom":
        return lazyGetSetting(state, "no-object-illustration-custom") as string;
      default:
        return noResultsSource;
    }
  },
});

export const PLUGIN_SELECTORS = getDefaultSelectors();

const getDefaultFormWidgets = (): Record<string, ComponentType<any>> => ({});

export const PLUGIN_FORM_WIDGETS = getDefaultFormWidgets();

const getDefaultSnippetSidebarPlusMenuOptions = () => [];
const getDefaultSnippetSidebarRowRenderers = () => ({});
const getDefaultSnippetSidebarModals = () => [];
const getDefaultSnippetSidebarHeaderButtons = () => [];

export const PLUGIN_SNIPPET_SIDEBAR_PLUS_MENU_OPTIONS =
  getDefaultSnippetSidebarPlusMenuOptions();
export const PLUGIN_SNIPPET_SIDEBAR_ROW_RENDERERS =
  getDefaultSnippetSidebarRowRenderers();
export const PLUGIN_SNIPPET_SIDEBAR_MODALS = getDefaultSnippetSidebarModals();
export const PLUGIN_SNIPPET_SIDEBAR_HEADER_BUTTONS =
  getDefaultSnippetSidebarHeaderButtons();

const getDefaultDashboardSubscriptionParametersSectionOverride =
  (): PluginDashboardSubscriptionParametersSectionOverride => ({
    Component: undefined,
  });

export const PLUGIN_DASHBOARD_SUBSCRIPTION_PARAMETERS_SECTION_OVERRIDE =
  getDefaultDashboardSubscriptionParametersSectionOverride();

const getDefaultReducers = () => ({
  applicationPermissionsPlugin: () => null,
  sandboxingPlugin: () => null,
  shared: () => null,
  metabotPlugin: () => null,
  documents: () => null,
  remoteSyncPlugin: () => null,
});

export const PLUGIN_REDUCERS: {
  applicationPermissionsPlugin: any;
  sandboxingPlugin: any;
  shared: any;
  metabotPlugin: any;
  documents: any;
  remoteSyncPlugin: any;
} = getDefaultReducers();

const getDefaultIsEeBuild = () => ({
  isEEBuild: () => false,
});

export const PLUGIN_IS_EE_BUILD = getDefaultIsEeBuild();

/**
 * @internal Do not call directly. Use the main reinitialize function from metabase/plugins instead.
 */
export function reinitialize() {
  PLUGIN_APP_INIT_FUNCTIONS.length = 0;
  PLUGIN_APP_INIT_FUNCTIONS.push(...getDefaultAppInitFunctions());

  Object.assign(PLUGIN_LANDING_PAGE, getDefaultLandingPage());

  PLUGIN_REDUX_MIDDLEWARES.length = 0;
  PLUGIN_REDUX_MIDDLEWARES.push(...getDefaultReduxMiddlewares());

  PLUGIN_LOGO_ICON_COMPONENTS.length = 0;
  PLUGIN_LOGO_ICON_COMPONENTS.push(...getDefaultLogoIconComponents());

  PLUGIN_ADMIN_ALLOWED_PATH_GETTERS.length = 0;
  PLUGIN_ADMIN_ALLOWED_PATH_GETTERS.push(
    ...getDefaultAdminAllowedPathGetters(),
  );

  Object.assign(PLUGIN_ADMIN_TOOLS, getDefaultAdminTools());
  Object.assign(PLUGIN_SELECTORS, getDefaultSelectors());
  Object.assign(PLUGIN_FORM_WIDGETS, getDefaultFormWidgets());

  PLUGIN_SNIPPET_SIDEBAR_PLUS_MENU_OPTIONS.length = 0;
  PLUGIN_SNIPPET_SIDEBAR_PLUS_MENU_OPTIONS.push(
    ...getDefaultSnippetSidebarPlusMenuOptions(),
  );

  Object.assign(
    PLUGIN_SNIPPET_SIDEBAR_ROW_RENDERERS,
    getDefaultSnippetSidebarRowRenderers(),
  );

  PLUGIN_SNIPPET_SIDEBAR_MODALS.length = 0;
  PLUGIN_SNIPPET_SIDEBAR_MODALS.push(...getDefaultSnippetSidebarModals());

  PLUGIN_SNIPPET_SIDEBAR_HEADER_BUTTONS.length = 0;
  PLUGIN_SNIPPET_SIDEBAR_HEADER_BUTTONS.push(
    ...getDefaultSnippetSidebarHeaderButtons(),
  );

  Object.assign(
    PLUGIN_DASHBOARD_SUBSCRIPTION_PARAMETERS_SECTION_OVERRIDE,
    getDefaultDashboardSubscriptionParametersSectionOverride(),
  );
  Object.assign(PLUGIN_REDUCERS, getDefaultReducers());
  Object.assign(PLUGIN_IS_EE_BUILD, getDefaultIsEeBuild());
}
